import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/lib/supabase';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { CreateTaskSheet } from '@/pages/tasks/CreateTaskSheet';
import {
  Plus,
  CheckSquare,
  MapPin,
  MessageSquare,
  FileText,
  Users,
  Calendar,
  ClipboardList,
  Settings,
  ChevronDown,
  Check
} from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';

// Button action configurations
const BUTTON_ACTIONS = {
  'create-task': {
    label: 'Create Task',
    icon: CheckSquare,
    color: 'bg-blue-600 hover:bg-blue-700',
    needsSpace: true,
    action: 'modal', // opens CreateTaskSheet
  },
  'new-checkin': {
    label: 'Check In',
    icon: MapPin,
    color: 'bg-green-600 hover:bg-green-700',
    needsSpace: true,
    action: 'navigate',
    getPath: (spaceId) => `/space/${spaceId}/checkins`,
  },
  'new-message': {
    label: 'New Message',
    icon: MessageSquare,
    color: 'bg-purple-600 hover:bg-purple-700',
    needsSpace: false,
    action: 'navigate',
    path: '/chat',
  },
  'new-booking': {
    label: 'New Booking',
    icon: Calendar,
    color: 'bg-orange-600 hover:bg-orange-700',
    needsSpace: true,
    action: 'navigate',
    getPath: (spaceId) => `/space/${spaceId}/bookings`,
  },
  'view-team': {
    label: 'View Team',
    icon: Users,
    color: 'bg-indigo-600 hover:bg-indigo-700',
    needsSpace: false,
    action: 'navigate',
    path: '/company-team',
  },
  'view-checklists': {
    label: 'Checklists',
    icon: ClipboardList,
    color: 'bg-teal-600 hover:bg-teal-700',
    needsSpace: true,
    action: 'navigate',
    getPath: (spaceId) => `/space/${spaceId}/checklists`,
  },
};

export function ActionButtonTile({ filter, onConfigChange, compact = false }) {
  const navigate = useNavigate();
  const { userProfile, company } = useAuth();
  const [spaces, setSpaces] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sheetOpen, setSheetOpen] = useState(false);

  const actionType = filter?.actionType || 'create-task';
  const selectedSpace = filter?.spaceId || '';
  const actionConfig = BUTTON_ACTIONS[actionType];

  useEffect(() => {
    if (!company?.id) {
      setLoading(false);
      return;
    }

    const loadSpaces = async () => {
      try {
        const { data, error } = await supabase
          .from('projects')
          .select('id, name')
          .eq('company_id', company.id)
          .eq('is_archived', false)
          .order('name');

        if (error) throw error;
        setSpaces(data || []);

        // Auto-select first space if none selected
        if (data?.length > 0 && !selectedSpace) {
          onConfigChange?.({ ...filter, spaceId: data[0].id });
        }
      } catch (err) {
        console.error('Failed to load spaces:', err);
      } finally {
        setLoading(false);
      }
    };

    loadSpaces();
  }, [company?.id]);

  const handleCreateTask = async (taskData) => {
    if (!selectedSpace || !userProfile?.id) return;

    try {
      const { error } = await supabase
        .from('tasks')
        .insert({
          title: taskData.name,
          description: taskData.description || null,
          project_id: selectedSpace,
          created_by: userProfile.id,
          assigned_to: taskData.assignedTo || [userProfile.id],
          status: 'todo',
          due_date: taskData.dueDate || null,
          priority: taskData.priority || null,
          subtasks: taskData.subtasks || [],
          attachments: taskData.attachments || [],
          recurrence_type: taskData.recurrence_type || null,
          recurrence_interval: taskData.recurrence_interval || null,
        });

      if (error) throw error;
      toast.success('Task created successfully');
    } catch (err) {
      console.error('Failed to create task:', err);
      toast.error('Failed to create task');
    }
  };

  const handleClick = () => {
    if (!actionConfig) return;

    if (actionConfig.action === 'modal') {
      if (actionType === 'create-task') {
        setSheetOpen(true);
      }
    } else if (actionConfig.action === 'navigate') {
      const path = actionConfig.needsSpace
        ? actionConfig.getPath?.(selectedSpace)
        : actionConfig.path;
      if (path) navigate(path);
    }
  };

  const Icon = actionConfig?.icon || Plus;

  // Configuration mode - show settings (only in non-compact mode)
  if (!filter?.actionType && !compact) {
    return (
      <div className="h-full flex flex-col items-center justify-center gap-3 p-2">
        <Settings className="h-6 w-6 text-gray-400" />
        <p className="text-sm text-gray-500 text-center">Configure button action</p>
        <Select
          value={actionType}
          onValueChange={(value) => onConfigChange?.({ ...filter, actionType: value })}
        >
          <SelectTrigger className="w-full max-w-[180px]">
            <SelectValue placeholder="Select action" />
          </SelectTrigger>
          <SelectContent>
            {Object.entries(BUTTON_ACTIONS).map(([key, config]) => {
              const ActionIcon = config.icon;
              return (
                <SelectItem key={key} value={key}>
                  <div className="flex items-center gap-2">
                    <ActionIcon className="h-4 w-4" />
                    {config.label}
                  </div>
                </SelectItem>
              );
            })}
          </SelectContent>
        </Select>
      </div>
    );
  }

  // Compact mode - just render the button inline
  if (compact) {
    const selectedSpaceName = spaces.find(s => s.id === selectedSpace)?.name;

    // For actions that need a space, show dropdown button
    if (actionConfig?.needsSpace) {
      return (
        <>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                className={cn(
                  "gap-2 text-white shadow-sm",
                  actionConfig?.color || 'bg-blue-600 hover:bg-blue-700'
                )}
              >
                <Icon className="h-4 w-4" />
                {actionConfig?.label || 'Action'}
                <ChevronDown className="h-3 w-3 ml-1 opacity-70" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start">
              <DropdownMenuLabel className="text-xs text-gray-500">
                Select space
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              {spaces.map((space) => (
                <DropdownMenuItem
                  key={space.id}
                  onClick={() => {
                    onConfigChange?.({ ...filter, spaceId: space.id });
                    // Execute action after selecting space
                    setTimeout(() => {
                      if (actionConfig.action === 'modal' && actionType === 'create-task') {
                        setSheetOpen(true);
                      } else if (actionConfig.action === 'navigate') {
                        const path = actionConfig.getPath?.(space.id);
                        if (path) navigate(path);
                      }
                    }, 50);
                  }}
                  className="flex items-center justify-between"
                >
                  {space.name}
                  {space.id === selectedSpace && (
                    <Check className="h-4 w-4 text-green-600" />
                  )}
                </DropdownMenuItem>
              ))}
              {spaces.length === 0 && (
                <DropdownMenuItem disabled>
                  No spaces available
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>

          {/* Create Task Sheet */}
          {actionType === 'create-task' && (
            <CreateTaskSheet
              open={sheetOpen}
              onOpenChange={setSheetOpen}
              onCreateTask={handleCreateTask}
              projectId={selectedSpace}
            />
          )}
        </>
      );
    }

    // For actions that don't need a space, simple button
    return (
      <>
        <Button
          onClick={handleClick}
          className={cn(
            "gap-2 text-white shadow-sm",
            actionConfig?.color || 'bg-blue-600 hover:bg-blue-700'
          )}
        >
          <Icon className="h-4 w-4" />
          {actionConfig?.label || 'Action'}
        </Button>

        {/* Create Task Sheet */}
        {actionType === 'create-task' && (
          <CreateTaskSheet
            open={sheetOpen}
            onOpenChange={setSheetOpen}
            onCreateTask={handleCreateTask}
            projectId={selectedSpace}
          />
        )}
      </>
    );
  }

  // Full mode with space selector
  return (
    <div className="h-full flex flex-col items-center justify-center gap-2 p-2">
      {/* Space selector if needed */}
      {actionConfig?.needsSpace && spaces.length > 0 && (
        <Select
          value={selectedSpace}
          onValueChange={(value) => onConfigChange?.({ ...filter, spaceId: value })}
        >
          <SelectTrigger className="w-full max-w-[180px] h-8 text-xs">
            <SelectValue placeholder="Select space" />
          </SelectTrigger>
          <SelectContent>
            {spaces.map((space) => (
              <SelectItem key={space.id} value={space.id}>
                {space.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      )}

      {/* Main action button */}
      <Button
        onClick={handleClick}
        disabled={actionConfig?.needsSpace && !selectedSpace}
        className={cn(
          "gap-2 text-white shadow-md",
          actionConfig?.color || 'bg-blue-600 hover:bg-blue-700'
        )}
        size="lg"
      >
        <Icon className="h-5 w-5" />
        {actionConfig?.label || 'Action'}
      </Button>

      {/* Create Task Sheet */}
      {actionType === 'create-task' && (
        <CreateTaskSheet
          open={sheetOpen}
          onOpenChange={setSheetOpen}
          onCreateTask={handleCreateTask}
          projectId={selectedSpace}
        />
      )}
    </div>
  );
}

// Export button actions for reference
export { BUTTON_ACTIONS };
